/**
 * safeArea.js - the one place that knows about notches, Dynamic Islands and
 * home indicators.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Around twenty page files each hand-rolled their own `env(safe-area-inset-*)`
 * expression, with different fallbacks, different base padding and different
 * opinions about whether to use `max()` or `calc()`. That is how one device
 * ends up with a back button under the Dynamic Island and another with a CTA
 * under the home indicator. Import from here instead of writing `env()` again.
 *
 * All of this depends on `viewport-fit=cover` in index.html. Without it every
 * `env(safe-area-inset-*)` resolves to `0px` and none of these helpers do
 * anything. Do not remove it.
 *
 * CSS EQUIVALENTS
 * ---------------
 * Everything here has a class-based twin in src/index.css, which is usually the
 * better choice inside JSX. The full set, which doubles as the list Tailwind
 * scans so every one of them is guaranteed to be emitted:
 *
 *   .safe-top .safe-bottom .safe-x            inset padding, one axis at a time
 *   .page-safe .page-safe-nav                 vertical insets for a page shell
 *   .pb-nav .pb-nav-total                     clears the bottom nav (synonyms)
 *   .ttt-container                            640px column, gutters, centred
 *   .ttt-container-narrow                     480px column
 *   .ttt-container-wide                       960px column
 *   .fill-viewport                            100dvh, with a vh fallback
 *   .fill-viewport-stable                     100svh, for a fixed full-bleed hero
 *
 * Use the inline styles below when the value has to be composed in JS (an
 * animated element, a measured height, a `style` prop that already exists).
 *
 * DEVICE NOTES
 * ------------
 * Portrait iPhone insets are roughly: top 44-62px (notch vs Dynamic Island),
 * bottom 34px (home indicator), left and right 0. iPad insets are 24px top and
 * 20px bottom with 0 sides. Landscape iPad and Split View can produce non-zero
 * left and right insets, which is why the horizontal helpers are not optional
 * decoration.
 */

/* ------------------------------------------------------------------ *
 * Raw inset expressions. Always fall back to 0px: an unprefixed
 * `env(safe-area-inset-top)` with no fallback makes the whole
 * declaration invalid on a browser that does not know the keyword.
 * ------------------------------------------------------------------ */

export const SAFE_TOP = 'env(safe-area-inset-top, 0px)';
export const SAFE_RIGHT = 'env(safe-area-inset-right, 0px)';
export const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)';
export const SAFE_LEFT = 'env(safe-area-inset-left, 0px)';

/**
 * Bottom navigation chrome height, in CSS pixels, excluding the home indicator.
 * Kept in sync with `--nav-height` in index.css and BottomNavigation.jsx.
 */
export const NAV_HEIGHT = 72;

/* ------------------------------------------------------------------ *
 * Composers. Prefer these to string-concatenating env() yourself.
 * ------------------------------------------------------------------ */

/**
 * Inset plus a fixed amount of breathing room.
 * @param {'top'|'right'|'bottom'|'left'} side
 * @param {string} extra CSS length added on top of the inset, e.g. '0.75rem'.
 * @returns {string} a `calc()` expression.
 * @example safeInset('bottom', '1rem') // 'calc(1rem + env(safe-area-inset-bottom, 0px))'
 */
export const safeInset = (side, extra = '0px') =>
  `calc(${extra} + env(safe-area-inset-${side}, 0px))`;

/**
 * Whichever is larger, the inset or a floor you always want.
 *
 * Use this when the element needs a guaranteed minimum gap even on a device
 * with no inset at all (an iPad, a desktop browser, an old iPhone SE). Use
 * `safeInset` instead when the padding should stack on top of the inset.
 * @param {'top'|'right'|'bottom'|'left'} side
 * @param {string} floor CSS length, e.g. '1rem'.
 * @returns {string} a `max()` expression.
 */
export const safeAtLeast = (side, floor) =>
  `max(${floor}, env(safe-area-inset-${side}, 0px))`;

/* ------------------------------------------------------------------ *
 * Ready-made style objects.
 * ------------------------------------------------------------------ */

/**
 * Keeps top controls (back arrow, bug button, page title) clear of the status
 * bar and the Dynamic Island without over-shifting a layout on a device that
 * has no notch.
 *
 * Unchanged behaviour from the original export: still `max(0.75rem, 0.5rem + inset)`.
 */
export const topSafePaddingStyle = {
  paddingTop: 'max(0.75rem, calc(0.5rem + env(safe-area-inset-top, 0px)))',
};

/** Bottom counterpart of `topSafePaddingStyle`. Clears the home indicator. */
export const bottomSafePaddingStyle = {
  paddingBottom: 'max(1rem, calc(0.75rem + env(safe-area-inset-bottom, 0px)))',
};

/**
 * Left and right insets. Zero in iPhone portrait, non-zero on a landscape iPad
 * and in some Split View configurations, so full-bleed rows still need it.
 */
export const horizontalSafePaddingStyle = {
  // max(), never the raw inset. These are applied as an inline style, which
  // beats any px-* class on the same element. The inset is 0px on every iPhone
  // in portrait, so setting it raw silently deleted the side gutter and pushed
  // body text flush against both screen edges. Matches .ttt-container in
  // index.css, which already does max(var(--gutter), var(--safe-left)).
  paddingLeft: 'max(var(--gutter, 1rem), env(safe-area-inset-left, 0px))',
  paddingRight: 'max(var(--gutter, 1rem), env(safe-area-inset-right, 0px))',
};

/**
 * The default page shell padding: safe on all four sides, with a comfortable
 * floor so content never touches a screen edge.
 *
 * Unchanged behaviour from the original export for top and bottom; the left and
 * right insets are new and are `0px` on every iPhone in portrait, so nothing
 * moves on the devices this was already tuned for.
 */
export const pageContentSafeAreaStyle = {
  ...topSafePaddingStyle,
  paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
  // max(), never the raw inset. These are applied as an inline style, which
  // beats any px-* class on the same element. The inset is 0px on every iPhone
  // in portrait, so setting it raw silently deleted the side gutter and pushed
  // body text flush against both screen edges. Matches .ttt-container in
  // index.css, which already does max(var(--gutter), var(--safe-left)).
  paddingLeft: 'max(var(--gutter, 1rem), env(safe-area-inset-left, 0px))',
  paddingRight: 'max(var(--gutter, 1rem), env(safe-area-inset-right, 0px))',
};

/**
 * Same as `pageContentSafeAreaStyle`, but leaves room for the fixed bottom
 * navigation as well. Use on any scrolling page that renders BottomNavigation,
 * otherwise the last row of content sits permanently under the tab bar.
 */
export const pageWithNavSafeAreaStyle = {
  ...topSafePaddingStyle,
  paddingBottom: `calc(${NAV_HEIGHT}px + 1.5rem + env(safe-area-inset-bottom, 0px))`,
  // max(), never the raw inset. These are applied as an inline style, which
  // beats any px-* class on the same element. The inset is 0px on every iPhone
  // in portrait, so setting it raw silently deleted the side gutter and pushed
  // body text flush against both screen edges. Matches .ttt-container in
  // index.css, which already does max(var(--gutter), var(--safe-left)).
  paddingLeft: 'max(var(--gutter, 1rem), env(safe-area-inset-left, 0px))',
  paddingRight: 'max(var(--gutter, 1rem), env(safe-area-inset-right, 0px))',
};

/**
 * For an element pinned to the bottom of the viewport (a sticky CTA, a compose
 * bar, a sheet handle). Sits above the home indicator on iPhone and above the
 * inset on iPad.
 */
export const stickyBottomSafeAreaStyle = {
  paddingBottom: 'max(1rem, calc(0.75rem + env(safe-area-inset-bottom, 0px)))',
  paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
  paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
};

/**
 * For an element pinned to the top of the viewport (a sticky header, a floating
 * back button). Mirror of `stickyBottomSafeAreaStyle`.
 */
export const stickyTopSafeAreaStyle = {
  paddingTop: 'max(0.75rem, calc(0.5rem + env(safe-area-inset-top, 0px)))',
  paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
  paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
};

/* ------------------------------------------------------------------ *
 * Viewport height.
 * ------------------------------------------------------------------ */

/**
 * Full-viewport minimum height that survives mobile browser chrome.
 *
 * `100vh` on iOS is the LARGE viewport: it stays at the height the page would
 * have with the URL bar hidden, so a `100vh` hero is taller than the screen and
 * its bottom is clipped until you scroll. `100dvh` tracks the visible area.
 *
 * A JS style object cannot hold two values for one property, so this has no
 * built-in `100vh` fallback for iOS 15.0 to 15.3 (which is inside our
 * deployment target and predates `dvh`). Prefer the `.fill-viewport` class or
 * Tailwind's `min-h-screen` from src/index.css, both of which do carry the
 * fallback. Use this object only where a class is genuinely not available.
 */
export const fillViewportStyle = {
  minHeight: '100dvh',
};

/**
 * Height expression for a viewport-filling element.
 * @param {'d'|'s'|'l'} kind
 *   'd' dynamic, shrinks and grows with the browser chrome. Default, right for
 *       a `min-height` page shell.
 *   's' small, the height with the chrome VISIBLE. Right for a fixed full-bleed
 *       hero, because it never causes the layout to resize mid-scroll.
 *   'l' large, the height with the chrome HIDDEN. Equivalent to `100vh`.
 * @param {string} subtract optional CSS length to take off, e.g. '72px'.
 */
export const viewportHeight = (kind = 'd', subtract = null) => {
  const unit = `100${kind}vh`;
  return subtract ? `calc(${unit} - ${subtract})` : unit;
};
